import React, { useState, useEffect } from 'react'
import styled, { css, keyframes } from 'styled-components'

export type PopupType = 'delete' | 'save' | 'confirm'

interface ConfirmPopupProps {
  isOpen?: boolean
  onClose: () => void
  onConfirm: () => Promise<void> | void
  title: string
  icon?: React.ReactNode
  confirmText?: string
  cancelText?: string
  type?: PopupType
  successText?: string // [추가] 성공 메시지를 직접 지정하고 싶을 때 사용
}

const ConfirmPopup = ({
  onClose,
  onConfirm,
  title,
  icon,
  confirmText = '확인',
  cancelText = '취소',
  type = 'confirm',
  successText, // [추가]
}: ConfirmPopupProps) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')

  const handleConfirmClick = async () => {
    try {
      setStatus('loading')
      await onConfirm()
      setStatus('success')

      setTimeout(() => {
        onClose()
      }, 1500)
    } catch (error) {
      console.error(error)
      setStatus('idle')
    }
  }

  useEffect(() => {
    return () => setStatus('idle')
  }, [])

  // [추가] 성공 메시지 결정 로직
  // 1. props로 successText가 들어왔으면 그걸 씀
  // 2. 아니면 type이 'delete'일 때 "삭제가..."
  // 3. 그 외에는 "저장이..."
  const getSuccessMessage = () => {
    if (successText) return successText
    if (type === 'delete') return '삭제가 완료되었습니다.'
    return '저장이 완료되었습니다.'
  }

  return (
    <Overlay>
      <Wrapper onClick={(e) => e.stopPropagation()}>
        {/* === CASE 1: 로딩 중 === */}
        {status === 'loading' && (
          <StatusContent>
            <Spinner />
            <Typo>
              {type === 'delete' ? '삭제 중입니다...' : '저장 중입니다...'}
            </Typo>
          </StatusContent>
        )}

        {/* === CASE 2: 성공 === */}
        {status === 'success' && (
          <StatusContent>
            <CheckIcon viewBox="0 0 24 24">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
            </CheckIcon>
            {/* [수정] 위에서 만든 함수로 메시지 출력 */}
            <Typo>{getSuccessMessage()}</Typo>
          </StatusContent>
        )}

        {/* === CASE 3: 일반 상태 === */}
        {status === 'idle' && (
          <>
            {icon && <IconWrapper>{icon}</IconWrapper>}
            <Typo>{title}</Typo>
            <BtnGroup>
              <BtnWrapper actionType="cancel" onClick={onClose}>
                {cancelText}
              </BtnWrapper>
              <BtnWrapper actionType={type} onClick={handleConfirmClick}>
                {confirmText}
              </BtnWrapper>
            </BtnGroup>
          </>
        )}
      </Wrapper>
    </Overlay>
  )
}

export default ConfirmPopup

// ... (스타일 컴포넌트들은 기존과 동일하게 유지) ...

// --- Styled Components ---

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 18px 16px;
  gap: 16px;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  width: 338px;
  min-height: 193px;
  height: auto;

  background: #ffffff;
  box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.16);
  border-radius: 14px;
  z-index: 1000;
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const Typo = styled.div`
  width: 100%;
  padding: 0 10px;
  font-family: 'SUIT';
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  text-align: center;
  word-break: keep-all;
  color: #333333;
`

const BtnGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
  margin-top: 5px;
`

interface BtnProps {
  actionType: 'cancel' | PopupType
}

const BtnWrapper = styled.div<BtnProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 148px;
  height: 49px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  ${({ actionType }) => {
    switch (actionType) {
      case 'delete':
        return css`
          background: #f7dcdc;
          color: #f36464;
        `
      case 'save':
      case 'confirm':
        return css`
          background: #e3f2fd;
          color: #2196f3;
        `
      case 'cancel':
      default:
        return css`
          background: #f5f5f5;
          color: #4d4d4d;
        `
    }
  }}
`

// --- [추가] 로딩/성공 상태용 스타일 ---

const StatusContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  height: 100%;
  padding: 20px 0;
`

// 로딩 애니메이션 정의
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3; /* 회색 배경 */
  border-top: 4px solid #2196f3; /* 파란색 회전 부분 */
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

const CheckIcon = styled.svg`
  width: 50px;
  height: 50px;
  fill: #10b981; /* 성공 초록색 */
`
